import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import questionFunctions from '../../utils/functions/question';
import rules from '../../utils/constants/rules';
import claimBankDetailsQuestionCodes from '../../utils/constants/claimBankDetailsQuestionsCodes';
import HEADER_FUNCTIONS from '../../utils/functions/headers';
import moment from 'moment';
import i18n from '../../i18n/i18n';

export const getQuestionFieldInputProps = (formikArgs, restArgs) => {
    const { field, values, setFieldValue, errors, meta, setFieldTouched } =
        formikArgs;
    const {
        question,
        questionTypeCode,
        handleCurrencySelectWrapper,
        currency,
        currencyOptions,
        onAutoSaveDraftClaim,
        token,
        inlineReadOnlyElement,
        formGroupVariant,
        inlineGroupElements,
        questions,
    } = restArgs;

    const questionOptions = [
        {
            key: '',
            label: '',
        },
        ...question.options.map(option => ({
            key: option.id,
            label: option.name,
        })),
    ];

    const isRequired = question.requiredByOptionId
        ? questionFunctions.requiredQuestionByOptionId(
              question,
              values,
              questions,
          )
        : question.isRequired;

    const handleBlur =
        (field, { values }) =>
        evt => {
            field.onBlur(evt);
            setFieldTouched(field, true);
            if (onAutoSaveDraftClaim) {
                onAutoSaveDraftClaim(values, meta);
            }
        };

    const handleAsyncSearchChange = value => {
        if (question.relatedQuestions && question.relatedQuestions.Address1) {
            setFieldValue(
                question.relatedQuestions.Address1,
                value.lines.addressline1,
            );
            const address1Input = document.querySelector(
                `input[name="${question.relatedQuestions.Address1}"]`,
            );
            if (address1Input) {
                if (value.lines.addressline1) {
                    address1Input.setCustomValidity('');
                }
                address1Input.blur();
            }
            setFieldValue(
                question.relatedQuestions.Address2,
                value.lines.addressline2,
            );
            setFieldValue(
                question.relatedQuestions.Address3,
                value.lines.addressline3,
            );
            setFieldValue(question.relatedQuestions.County, value.county);
            const townInput = document.querySelector(
                `input[name="${question.relatedQuestions.Town}"]`,
            );
            if (townInput) {
                if (value.town) {
                    townInput.setCustomValidity('');
                }
                townInput.blur();
            }
            setFieldValue(question.relatedQuestions.Town, value.town);

            if (question.relatedQuestions.Province) {
                const controlName = question.relatedQuestions.Province;
                const dropdownElement = document.querySelector(
                    `select[name="${controlName}"]`,
                );
                if (dropdownElement) {
                    const options = Array.from(dropdownElement.options);
                    const selectedOption = options.find(
                        option => option.text === value.province,
                    );

                    if (selectedOption) {
                        dropdownElement.value = selectedOption.value;
                        dropdownElement.dispatchEvent(
                            new Event('change', { bubbles: true }),
                        );
                    }
                }
            }
        }
        setFieldValue(field.name, value.postcode);
    };

    const generalInputProps = {
        id: question.code.replace('-', '_'),
        name: question.code,
        options: questionOptions,
        required: isRequired,
        autoComplete: 'off',
        value: field.value || '',
        onBlur: handleBlur(field, { values }),
        checked: !!(field.value && field.value.length > 0),
        onInvalid: e => {
            e.target.setCustomValidity(i18n.t('requiredField'));
        },
        onInput: e => {
            e.target.setCustomValidity('');
        },
        title: isRequired ? i18n.t('requiredField') : '',
    };

    if (formGroupVariant === 'plain') {
        return {
            id: '_question_' + question.code.replace('-', '_'),
            name: '_question' + question.code,
            label: inlineReadOnlyElement
                ? inlineReadOnlyElement
                : field?.value instanceof Date
                  ? moment(field.value).format('DD/MM/YYYY')
                  : field.value,
        };
    }

    if (
        formGroupVariant === 'radioButtonGroup' &&
        question.displayType === 'horizontal-radio'
    ) {
        let options = question.options.map(item => {
            //handle overlapping options with the same ID in React
            return {
                id: `${question.id}//${item.id}`,
                label: item.name,
            };
        });
        return {
            ...generalInputProps,
            horizontal: true,
            name: question.code,
            options: options,
            onChange: e => setFieldValue(field.name, e),
            style: { height: '24px', width: '24px', flex: 1 },
            labelStyle: { marginTop: '10px', height: '80%' },
        };
    }

    if (
        formGroupVariant === 'radioButtonGroup' &&
        question.displayType === 'vertical-radio'
    ) {
        let options = question.options.map(item => {
            //handle overlapping options with the same ID in React
            return {
                label: item.name,
                id: `${question.id}//${item.id}`,
            };
        });
        return {
            ...generalInputProps,
            options: options,
            onChange: e => {
                setFieldValue(field.name, e);
            },
            id: question.code.replace('-', '_'),
        };
    }
    if (formGroupVariant === 'buttonRadioGroup') {
        const ranging = questionFunctions.getQuestionIntegerRange(question);
        const rangeOptions = [
            ...GENERAL_FUNCTIONS.rangeArray(
                ranging.minValue,
                ranging.maxValue,
            ).map(range => ({
                value: range,
                label: range,
                description:
                    ranging.minValue === range
                        ? question.startLabel
                        : ranging.maxValue === range
                          ? question.endLabel
                          : null,
            })),
        ];
        return {
            ...generalInputProps,
            onClick: value => setFieldValue(field.name, value.value),
            items: rangeOptions,
            style: { textAlign: 'center', width: '89px', marginTop: '10px' },
            value: field.value,
        };
    }

    if (formGroupVariant === 'postcodeSearchableList') {
        const setPostCodeValue = value => {
            setFieldValue(field.name, value);
        };

        return {
            ...generalInputProps,
            inputValue: field.value || '',
            timeOutInMilliseconds: 5000,
            inputId: question.code.replace('-', '_'),
            onChange: handleAsyncSearchChange,
            setInputValue: setPostCodeValue,
            url: `${process.env.REACT_APP_BASE_URL}api/Services/addresses/postCode/${field.value}`,
            headers: HEADER_FUNCTIONS.headersWithTokenJson(token),
            placeholder: '',
            noOptionsMessage: i18n.t('noOptions'),
        };
    }

    if (formGroupVariant === 'date') {
        const dateInputProps =
            questionFunctions.mapQuestionTypeCodeToDateInputProps(
                questionTypeCode,
                question,
            );

        const onDateTimeChange = value => {
            let newState = {
                ...values,
                [field.name]: value,
            };
            if (onAutoSaveDraftClaim) {
                onAutoSaveDraftClaim(newState, meta);
            }
            setFieldValue(field.name, value);
        };

        const { minDate, maxDate } = questionFunctions.dateQuestionRestrictions(
            question,
            values,
        );
        return {
            ...generalInputProps,
            dateInputProps: {
                ...generalInputProps,
                ...dateInputProps,
                minDate: minDate
                    ? GENERAL_FUNCTIONS.adjustMinMaxToLocalTime(minDate)
                    : null,
                maxDate: maxDate
                    ? GENERAL_FUNCTIONS.adjustMinMaxToLocalTime(maxDate)
                    : null,
                dateFormat: 'dd/MM/yyyy',
                onChange: onDateTimeChange,
                selected: field.value
                    ? new Date(
                          GENERAL_FUNCTIONS.convertToParsedDateStringFormat(
                              field.value,
                          ),
                      )
                    : '',
            },
        };
    }

    if (formGroupVariant === 'textarea') {
        let maxLengthRule;
        let charLimit;
        if (question.questionValidationRules) {
            maxLengthRule = Object.entries(
                question.questionValidationRules,
            ).find(([key, value]) => {
                return key === rules.maxLength.code && value.length > 0;
            });
        }
        charLimit = maxLengthRule
            ? maxLengthRule[1].reduce((acc, curr) => {
                  return parseInt(curr.value) > acc
                      ? parseInt(curr.value)
                      : curr;
              }, -1)
            : null;
        return charLimit && charLimit >= 0
            ? {
                  ...generalInputProps,
                  isVertical: true,
                  maxCharacters: charLimit,
              }
            : { ...generalInputProps, isVertical: true };
    }

    if (formGroupVariant === 'currency') {
        const menuoptions = currencyOptions?.map(x => ({
            code: x.name,
            sign: x.symbol,
        }));
        const defaultCurrency = currencyOptions?.find(
            x => x.isDefault === true,
        );
        const handleOptionSelect = evt => {
            handleCurrencySelectWrapper(evt, values);
        };
        return {
            ...generalInputProps,
            menuOptions: menuoptions,
            handleOptionSelect: handleOptionSelect,
            selectedCurrency: currency?.code
                ? currency
                : {
                      code: defaultCurrency?.name,
                      sign: defaultCurrency?.symbol,
                  },
        };
    }

    if (
        questionFunctions.isQuestionOfTypeIntegerRange(
            question,
            questionTypeCode,
        )
    ) {
        const range = questionFunctions.getQuestionIntegerRange(question);
        const rangeOptions = [
            {
                key: '',
                label: '',
            },
            ...GENERAL_FUNCTIONS.rangeArray(range.minValue, range.maxValue).map(
                range => ({
                    key: range,
                    label: range,
                }),
            ),
        ];

        return {
            ...generalInputProps,
            isVertical: true,
            options: rangeOptions,
        };
    }

    //add dashes in every 2 characters for bank details question sort code
    if (question.code === claimBankDetailsQuestionCodes.sortCode) {
        let valueWithHyphens = GENERAL_FUNCTIONS.addHyphenInEveryTwoCharacters(
            field.value,
        );
        const handleValueWithDashes = e => {
            setFieldValue(
                field.name,
                GENERAL_FUNCTIONS.removeHyphens(e.target.value),
            );
        };

        return {
            ...generalInputProps,
            value: valueWithHyphens,
            onChange: handleValueWithDashes,
        };
    }

    return generalInputProps;
};
